"use client";

import { useToast } from "@/context/ToastContext";
import { StaticDataType } from "@/utils/hooks/static/staticHooks";
import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

interface OptionsListProps {
  options: StaticDataType[];
  title: string;
  onOptionSelect?: (option: string) => void;
  excludeGenderOption3?: boolean; // Para excluir la opción 3 "Indiferente" en contextos de importación
}

export const OptionsList: React.FC<OptionsListProps> = ({
  options,
  title,
  onOptionSelect,
  excludeGenderOption3 = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  // Cerrar la lista al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  const handleOptionSelect = async (option: string) => {
    try {
      // Copiar al portapapeles
      await navigator.clipboard.writeText(option);

      // Mostrar toast de confirmación
      showToast({
        description: `"${option}" copiado al portapapeles`,
        variant: "success",
      });

      // Llamar callback si existe
      if (onOptionSelect) {
        onOptionSelect(option);
      }

      // Cerrar la lista
      setIsExpanded(false);
    } catch {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = option;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      showToast({
        description: `"${option}" copiado al portapapeles`,
        variant: "success",
      });

      if (onOptionSelect) {
        onOptionSelect(option);
      }

      setIsExpanded(false);
    }
  };

  // Filtrar opciones si es necesario
  const filteredOptions = excludeGenderOption3
    ? options.filter((option) => option.value !== 3)
    : options;

  if (filteredOptions.length === 0) return null;

  return (
    <div ref={containerRef} className="relative w-full">
      <span
        className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 whitespace-normal break-words cursor-pointer hover:bg-yellow-200 transition-colors duration-200 inline-flex items-center gap-1"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>{title}:</span>
        {isExpanded ? (
          <FiChevronUp className="h-3 w-3" />
        ) : (
          <FiChevronDown className="h-3 w-3" />
        )}
      </span>

      {isExpanded && (
        <div className="absolute z-10 h-24 overflow-y-auto border rounded p-1 bg-white shadow-lg text-xs min-w-48 top-full left-0 mt-1">
          <div className="grid grid-cols-1 gap-1">
            {filteredOptions.map((option, idx) => (
              <div
                key={idx}
                className="text-gray-700 hover:bg-gray-100 p-1 rounded cursor-pointer transition-colors duration-200"
                onClick={() => handleOptionSelect(option.label)}
                title={`Hacer clic para copiar "${option.label}"`}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Función para obtener el componente de opciones según el tipo de campo
export const getOptionsComponent = (
  fieldName: string,
  staticData: {
    ratePeriod?: { data: StaticDataType[] };
    invoiceFrequency?: { data: StaticDataType[] };
    invoiceTimeMode?: { data: StaticDataType[] };
    invoiceMode?: { data: StaticDataType[] };
    gender?: { data: StaticDataType[] };
  },
  onOptionSelect?: (option: string) => void,
  isImportContext: boolean = false // Para determinar si estamos en contexto de importación
) => {
  // Campos de período de tarifa
  if (
    fieldName.includes("rate_period") ||
    fieldName.includes("período") ||
    fieldName.includes("periodo")
  ) {
    return (
      <OptionsList
        options={staticData.ratePeriod?.data || []}
        title="Períodos disponibles"
        onOptionSelect={onOptionSelect}
      />
    );
  }

  // Campos de frecuencia de facturación
  if (
    fieldName.includes("invoice_frequency") ||
    fieldName.includes("frecuencia")
  ) {
    return (
      <OptionsList
        options={staticData.invoiceFrequency?.data || []}
        title="Frecuencias disponibles"
        onOptionSelect={onOptionSelect}
      />
    );
  }

  // Campos de tiempo de facturación
  if (fieldName.includes("invoice_time_mode") || fieldName.includes("tiempo")) {
    return (
      <OptionsList
        options={staticData.invoiceTimeMode?.data || []}
        title="Tiempos disponibles"
        onOptionSelect={onOptionSelect}
      />
    );
  }

  // Campos de modo de facturación
  if (fieldName.includes("invoice_mode") || fieldName.includes("modo")) {
    return (
      <OptionsList
        options={staticData.invoiceMode?.data || []}
        title="Modos disponibles"
        onOptionSelect={onOptionSelect}
      />
    );
  }

  // Campos de género
  if (fieldName.includes("gender") || fieldName.includes("género")) {
    return (
      <OptionsList
        options={staticData.gender?.data || []}
        title="Géneros disponibles"
        onOptionSelect={onOptionSelect}
        excludeGenderOption3={isImportContext} // Excluir opción 3 solo en contexto de importación
      />
    );
  }

  return null;
};

export default OptionsList;
